import React, { Dispatch, SetStateAction } from "react";
import {
  IFinancialAuditInput,
  IFinancialIncome,
  IFinancialLoan,
  IFinancialShare,
  IFinancialTax,
} from "../../exports/interfaces";
import { dynamicSuccess } from "../../hooks/useToast";
import Select from "react-select";
import { customSelectStyles } from "../../exports/customSelectStyles";
import { Upload } from "lucide-react";
import { useOption } from "../../context/OptionContext";
import {
  renderExistingFilePreview,
  renderFilePreview,
} from "../../exports/functions";

const Financials = ({
  financialsactiveTab,
  currentStep,
  shareFinancial,
  setShareFinancial,
  loanFinancial,
  setLoanFinancial,
  incomeFinancial,
  setIncomeFinancial,
  auditFinancial,
  setAuditFinancial,
  taxFinancial,
  setTaxFinancial,
  errors,
}: {
  financialsactiveTab: string;
  currentStep: number;
  shareFinancial: IFinancialShare;
  setShareFinancial: Dispatch<SetStateAction<IFinancialShare>>;
  loanFinancial: IFinancialLoan;
  setLoanFinancial: Dispatch<SetStateAction<IFinancialLoan>>;
  incomeFinancial: IFinancialIncome;
  setIncomeFinancial: Dispatch<SetStateAction<IFinancialIncome>>;
  auditFinancial: IFinancialAuditInput;
  setAuditFinancial: Dispatch<SetStateAction<IFinancialAuditInput>>;
  taxFinancial: IFinancialTax;
  setTaxFinancial: Dispatch<SetStateAction<IFinancialTax>>;
  errors: any;
}) => {
  const { categories } = useOption();

  const previewFile = auditFinancial.file;
  const previewDocumentUrl = auditFinancial.documentUrl;
  const previewFileName = auditFinancial.fileName || previewDocumentUrl || "";

  const handleShareChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setShareFinancial((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fiscalYearOptions =
    categories
      ?.find((item) => item.name === "Fiscal Year")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const handleLoanChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setLoanFinancial((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleIncomeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setIncomeFinancial((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAuditChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setAuditFinancial((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAuditFinancial((prev) => ({
        ...prev,
        fileName: file.name,
        file: file,
        documentUrl: "",
      }));
    }
  };

  const handleTaxChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setTaxFinancial((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDraft = () => {
    if (financialsactiveTab === "share") {
      localStorage.setItem("share", JSON.stringify(shareFinancial));
      dynamicSuccess("Share saved as draft!");
    } else if (financialsactiveTab === "loan") {
      localStorage.setItem("loan", JSON.stringify(loanFinancial));
      dynamicSuccess("Loan saved as draft!");
    } else if (financialsactiveTab === "income") {
      localStorage.setItem("income", JSON.stringify(incomeFinancial));
      dynamicSuccess("Income saved as draft!");
    } else if (financialsactiveTab === "audit") {
      const { file, fileName, ...rest } = auditFinancial;
      localStorage.setItem("audit", JSON.stringify(rest));
      dynamicSuccess("Audit saved as draft!");
    }
  };

  return (
    <>
      <div
        className={`dashboard-card ${currentStep !== 2 ? "mt-2" : ""}`}
        style={currentStep === 2 ? { borderTopLeftRadius: 0 } : {}}
      >
        <div className="row g-3">
          <div className="col-12">
            {financialsactiveTab === "share" && (
              <>
                <h5>
                  <span className="step-circle">1</span>Total share members,
                  share amount & saving
                </h5>
                <div className="row g-3">
                  <div className={`col-lg-6 col-md-4`}>
                    <label className="form-label">
                      Fiscal Year <span className="primary-color">*</span>
                    </label>
                    <Select
                      styles={customSelectStyles}
                      options={fiscalYearOptions}
                      placeholder="Select the Fiscal Year"
                      value={
                        fiscalYearOptions.find(
                          (option) =>
                            option.value === shareFinancial.fiscalYear,
                        ) || null
                      }
                      onChange={(selected) => {
                        setShareFinancial((prev: any) => ({
                          ...prev,
                          fiscalYear: selected?.value,
                        }));
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">
                    Membership & Share Information
                  </h5>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Male Members <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="maleMembers"
                        value={shareFinancial.maleMembers || ""}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Female Members <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="femaleMembers"
                        value={shareFinancial.femaleMembers || ""}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Other Members <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="otherMembers"
                        value={shareFinancial.otherMembers || ""}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Institutional Members{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="institutionalMembers"
                        value={shareFinancial.institutionalMembers || ""}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Share Members{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        name="totalShareMembers"
                        value={shareFinancial.totalShareMembers || ""}
                        onChange={handleShareChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">Savings Information</h5>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Total General Saving{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalGeneralSaving"
                        value={shareFinancial.totalGeneralSaving}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Regular Saving{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalRegularSaving"
                        value={shareFinancial.totalRegularSaving}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Periodic Saving{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalPeriodicSaving"
                        value={shareFinancial.totalPeriodicSaving}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Saving Amount{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        name="totalSavingAmount"
                        value={shareFinancial.totalSavingAmount}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Female Depositors{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="femaleDepositors"
                        value={shareFinancial.femaleDepositors}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Male Depositors <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="maleDepositors"
                        value={shareFinancial.maleDepositors}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Others Depositors{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="otherDepositors"
                        value={shareFinancial.otherDepositors}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Depositors{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Auto added total depositors"
                        name="totalDepositors"
                        value={shareFinancial.totalDepositors}
                        onChange={handleShareChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">Assets & Liabilities</h5>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Liabilities{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalLiabilities"
                        value={shareFinancial.totalLiabilities}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Assets <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalAssets"
                        value={shareFinancial.totalAssets}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total liquid assets (TLA){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalLiquidAssets"
                        value={shareFinancial.totalLiquidAssets}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        TLA to Total Assets (TA) ratio{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Auto added TLA and TA"
                        name="tlaToTaRatio"
                        value={shareFinancial.tlaToTaRatio}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Balance Sheet <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Auto added assets and labially "
                        name="balanceSheet"
                        value={shareFinancial.balanceSheet}
                        onChange={handleShareChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-2">
                  <h5 className="text-uppercase mb-4">Liquidity</h5>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Cash <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="cash"
                        value={shareFinancial.cash}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Association <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Association"
                        name="association"
                        value={shareFinancial.association}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        District Union <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter District Union"
                        name="districtUnion"
                        value={shareFinancial.districtUnion}
                        onChange={handleShareChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Bank / Cooperative{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Bank / Cooperative"
                        name="bank"
                        value={shareFinancial.bank}
                        onChange={handleShareChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex mt-4 justify-content-end">
                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleDraft}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
            {financialsactiveTab === "loan" && (
              <>
                <h5>
                  <span className="step-circle">2</span>Total funds & loan
                  details
                </h5>
                <div className="row g-3">
                  <div className={`col-lg-6 col-md-4`}>
                    <label className="form-label">
                      Fiscal Year <span className="primary-color">*</span>
                    </label>
                    <Select
                      styles={customSelectStyles}
                      options={fiscalYearOptions}
                      placeholder="Select the Fiscal Year"
                      value={
                        fiscalYearOptions.find(
                          (option) => option.value === loanFinancial.fiscalYear,
                        ) || null
                      }
                      onChange={(selected) => {
                        setLoanFinancial((prev: any) => ({
                          ...prev,
                          fiscalYear: selected?.value,
                        }));
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">Capital Information</h5>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Share capital{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalShareCapital"
                        value={loanFinancial.totalShareCapital}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total reserve fund{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalReserveFund"
                        value={loanFinancial.totalReserveFund}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Deficit replenishment fund{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="deficitReplenishmentFund"
                        value={loanFinancial.deficitReplenishmentFund}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Accumulated profit/(loss){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="accumulatedProfitLoss"
                        value={loanFinancial.accumulatedProfitLoss}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Core Capital <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        name="coreCapital"
                        value={loanFinancial.coreCapital}
                        onChange={handleLoanChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">loan Information</h5>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Total pass loan <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalPassLoan"
                        value={loanFinancial.totalPassLoan}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total sub-standard loan{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalSubStandardLoan"
                        value={loanFinancial.totalSubStandardLoan}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total doubtful loan{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalDoubtfulLoan"
                        value={loanFinancial.totalDoubtfulLoan}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total loss loan <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalLossLoan"
                        value={loanFinancial.totalLossLoan}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Loan disbursed{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        name="totalLoanDisbursed"
                        value={loanFinancial.totalLoanDisbursed}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        External Loan <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="externalLoan"
                        value={loanFinancial.externalLoan}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Male Borrowers <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="maleBorrowers"
                        value={loanFinancial.maleBorrowers}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Female Borrowers{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="femaleBorrowers"
                        value={loanFinancial.femaleBorrowers}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Others Borrowers{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="othersBorrowers"
                        value={loanFinancial.othersBorrowers}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Borrowers <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Auto added total borrowers"
                        name="totalBorrowers"
                        value={loanFinancial.totalBorrowers}
                        onChange={handleLoanChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">LLP Information</h5>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        LLP for pass loan(1%){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="llpPassLoan"
                        value={loanFinancial.llpPassLoan}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        LLP for sub-standard loan (25%){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="llpSubStandardLoan"
                        value={loanFinancial.llpSubStandardLoan}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        LLP for doubtful loan (50%){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="llpDoubtfulLoan"
                        value={loanFinancial.llpDoubtfulLoan}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        LLP for loss loan (100%){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="llpLossLoan"
                        value={loanFinancial.llpLossLoan}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total loan loss provision (LLP) amount{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        name="totalLoanLossProvision"
                        value={loanFinancial.totalLoanLossProvision}
                        onChange={handleLoanChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">
                    Supplementary Capital Information
                  </h5>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Additional provision of loan loss{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="additionalProvisionLoanLoss"
                        value={loanFinancial.additionalProvisionLoanLoss}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Assets revaluation reserve{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="assetsRevaluationReserve"
                        value={loanFinancial.assetsRevaluationReserve}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Free reserve <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="freeReserve"
                        value={loanFinancial.freeReserve}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Supplementry Capital{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        name="supplementaryCapital"
                        value={loanFinancial.supplementaryCapital}
                        onChange={handleLoanChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">
                    Total Loan and Capital Information
                  </h5>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Total capital fund{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalCapitalFund"
                        value={loanFinancial.totalCapitalFund}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total non performing loan{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalNonPerformingLoan"
                        value={loanFinancial.totalNonPerformingLoan}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total non performing loan %{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="nplPercentage"
                        value={loanFinancial.nplPercentage}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total risk weighted assets(RWA){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalRiskWeightedAssets"
                        value={loanFinancial.totalRiskWeightedAssets}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Core capital ratio{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="coreCapitalRatio"
                        value={loanFinancial.coreCapitalRatio}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total capital fund ratio{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalCapitalFundRatio"
                        value={loanFinancial.totalCapitalFundRatio}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total borrowing <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="totalBorrowing"
                        value={loanFinancial.totalBorrowing}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total No of loan file re-scheduled{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="loanRescheduledCount"
                        value={loanFinancial.loanRescheduledCount || ""}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total amount of loan re-scheduled{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="loanRescheduledAmount"
                        value={loanFinancial.loanRescheduledAmount}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Investment in Pvt. Ltd{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="investmentPrivateLtd"
                        value={loanFinancial.investmentPrivateLtd}
                        onChange={handleLoanChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Investment in house/building{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="investmentHouseBuilding"
                        value={loanFinancial.investmentHouseBuilding}
                        onChange={handleLoanChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex mt-4 justify-content-end">
                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleDraft}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
            {financialsactiveTab === "income" && (
              <>
                <h5>
                  <span className="step-circle">3</span>Income expenses & other
                  details
                </h5>
                <div className="row g-3">
                  <div className={`col-lg-6 col-md-4`}>
                    <label className="form-label">
                      Fiscal Year <span className="primary-color">*</span>
                    </label>
                    <Select
                      styles={customSelectStyles}
                      options={fiscalYearOptions}
                      placeholder="Select the Fiscal Year"
                      value={
                        fiscalYearOptions.find(
                          (option) =>
                            option.value === incomeFinancial.fiscalYear,
                        ) || null
                      }
                      onChange={(selected) => {
                        setIncomeFinancial((prev: any) => ({
                          ...prev,
                          fiscalYear: selected?.value,
                        }));
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Operational income{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="operationalIncome"
                        value={incomeFinancial.operationalIncome}
                        onChange={handleIncomeChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Other income <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="otherIncome"
                        value={incomeFinancial.otherIncome}
                        onChange={handleIncomeChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Operational expenses{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="operationalExpenses"
                        value={incomeFinancial.operationalExpenses}
                        onChange={handleIncomeChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Other expenses <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="otherExpenses"
                        value={incomeFinancial.otherExpenses}
                        onChange={handleIncomeChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Net profit/(Loss){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control with-spin"
                        placeholder="0"
                        name="netProfitLoss"
                        value={incomeFinancial.netProfitLoss}
                        onChange={handleIncomeChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex mt-4 justify-content-end">
                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleDraft}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
            {financialsactiveTab === "audit" && (
              <>
                <h5>
                  <span className="step-circle">4</span>Auditor & AGM details
                </h5>
                <div className="row g-3">
                  <div className={`col-lg-6 col-md-4`}>
                    <label className="form-label">
                      Fiscal Year <span className="primary-color">*</span>
                    </label>
                    <Select
                      styles={customSelectStyles}
                      options={fiscalYearOptions}
                      placeholder="Select the Fiscal Year"
                      value={
                        fiscalYearOptions.find(
                          (option) =>
                            option.value === auditFinancial.fiscalYear,
                        ) || null
                      }
                      onChange={(selected) => {
                        setAuditFinancial((prev: any) => ({
                          ...prev,
                          fiscalYear: selected?.value,
                        }));
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Auditor's Name <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Name here"
                        name="auditorName"
                        value={auditFinancial.auditorName}
                        onChange={handleAuditChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Auditor's mobile No.
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter mobile number here"
                        name="auditorMobile"
                        value={auditFinancial.auditorMobile}
                        onChange={handleAuditChange}
                      />
                      {errors.auditorMobile && (
                        <p className="error-message">{errors.auditorMobile}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Auditor's Address{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control with-spin"
                        placeholder="Enter Address here"
                        name="auditorAddress"
                        value={auditFinancial.auditorAddress}
                        onChange={handleAuditChange}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">
                        UDIN number <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter mobile number here"
                        name="icanNumber"
                        value={auditFinancial.icanNumber}
                        onChange={handleAuditChange}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">
                        Date of AGM <span className="primary-color">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        name="agmDate"
                        value={auditFinancial.agmDate}
                        onChange={handleAuditChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        No. of Participants in AGM
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        name="participants"
                        value={auditFinancial.participants || ""}
                        onChange={handleAuditChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Participants % in AGM{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control with-spin"
                        placeholder="0"
                        name="participantsPercent"
                        value={auditFinancial.participantsPercent}
                        onChange={handleAuditChange}
                      />
                    </div>
                  </div>
                </div>
                <div className={`dashboard-card mt-3`}>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="mb-3">
                        <h6>Audit Documents</h6>
                        <small>Audit reports and financial statements</small>
                      </div>
                      <label className="form-label">
                        Upload Document (PDF, JPEG, PNG)
                      </label>
                      {previewFile || previewDocumentUrl ? (
                        <div className="upload-box p-4">
                          {previewFile
                            ? renderFilePreview(
                                previewFile,
                                previewFileName,
                                handleFileChange,
                              )
                            : renderExistingFilePreview(
                                previewDocumentUrl,
                                handleFileChange,
                              )}
                        </div>
                      ) : (
                        <div className="upload-box text-center p-4">
                          <div className="mb-2">
                            <Upload />
                          </div>

                          <p className="mb-1">
                            Choose a file or drag and drop it here.
                          </p>

                          <small className="d-block mb-3">
                            (txt, docx, xlsx, pdf, jpeg, png - Up to 100 mb)
                          </small>

                          <label className="btn btn-badge text-main-color px-4 mb-0">
                            Browse file
                            <input
                              type="file"
                              hidden
                              name="file"
                              onChange={handleFileChange}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="d-flex mt-4 justify-content-end">
                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleDraft}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
            {financialsactiveTab === "tax" && (
              <>
                <h5>
                  <span className="step-circle">5</span>Tax Information
                </h5>
                <div className="row g-3">
                  <div className={`col-lg-6 col-md-4`}>
                    <label className="form-label">
                      Fiscal Year <span className="primary-color">*</span>
                    </label>
                    <Select
                      styles={customSelectStyles}
                      options={fiscalYearOptions}
                      placeholder="Select the Fiscal Year"
                      value={
                        fiscalYearOptions.find(
                          (option) => option.value === taxFinancial.fiscalYear,
                        ) || null
                      }
                      onChange={(selected) => {
                        setTaxFinancial((prev: any) => ({
                          ...prev,
                          fiscalYear: selected?.value,
                        }));
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Income Tax <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter This Years income Tax"
                        name="incomeTax"
                        value={taxFinancial.incomeTax}
                        onChange={handleTaxChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Interest Tax <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Interest Tax here"
                        name="interestTax"
                        value={taxFinancial.interestTax}
                        onChange={handleTaxChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Employee Tax <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Employee Tax here"
                        name="employeeTax"
                        value={taxFinancial.employeeTax}
                        onChange={handleTaxChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Others <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter any other required data here"
                        name="taxOthers"
                        value={taxFinancial.taxOthers}
                        onChange={handleTaxChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">Local Government</h5>
                  <div className="row g-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Service Tax <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Interest Tax here"
                        name="serviceTax"
                        value={taxFinancial.serviceTax}
                        onChange={handleTaxChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Rental Tax <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Interest Tax here"
                        name="rentalTax"
                        value={taxFinancial.rentalTax}
                        onChange={handleTaxChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Others <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter any other required data here"
                        name="localOthers"
                        value={taxFinancial.localOthers}
                        onChange={handleTaxChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex mt-4 justify-content-end">
                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleDraft}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {/* {financialsactiveTab === "audit" && auditData.length > 0 && (
        <AuditTable auditData={auditData} />
      )} */}
    </>
  );
};

export default Financials;
